<script setup lang="ts">
/**
 * **节点外壳** —— 图引擎那一层"定位盒子"（原来是 Vue Flow 的
 * `.vue-flow__node`）。
 *
 * ## 它只做三件事（别的都归 NpNode）
 *
 * ```text
 *   ① 用 transform 把节点放到画布坐标 (x, y)
 *   ② 挂契约属性 data-id（5 处代码按它找节点）
 *   ③ 抬升层级（选中 / 拖动中的节点要压在别的上面）
 * ```
 *
 * ⚠️ **外观一律由 `NpNode` 提供**：这里不写边框、不写背景、不写阴影。
 * 视觉层换皮时不该动到画布层（这条原来在 CSS 里靠一串 `border: 0` 之类
 * 的重置表达，现在换成"这里根本没有外观"）。
 *
 * ## 为什么用 `transform` 而不是 `left/top`
 *
 * 拖动时每帧都要改位置：`transform` 走合成层，`left/top` 会触发重排 ——
 * 节点一多就能感觉到差别（而 `transform` 的代价是**不能用来量偏移**：
 * `getBoundingClientRect` 已经把 transform 算进去了，这正好是我们要的）。
 */
import { computed } from 'vue'
import type { Point } from '../../types/geometry'

const props = withDefaults(
  defineProps<{
    id: string
    position: Point
    selected?: boolean
    dragging?: boolean
    /** 被框住 / 悬停的临时抬升（拖动另一批时不要盖住它们） */
    elevated?: boolean
    /**
     * **记下来的叠放次序**（模型上的 `z`）—— 越大越靠上。
     *
     * ⚠️ 和上面三个布尔**不是一回事**：那三个是"此刻的交互状态"，
     * 这个是**文档里的一个数**（选中一次就被赋成 max+1，之后一直在上面）。
     * 用户要的正是后者："选中节点应该默认到最顶，不需要再回到原来的层级"。
     */
    z?: number
    /**
     * 宿主给节点外壳的**附加样式**（`FlowNode.style` 原样透传）。
     *
     * ⚠️ 它必须透传：框节点靠 `{ pointerEvents: 'none' }` 让指针穿过自己
     * （否则一个占满屏幕的框会把里面的节点全挡住 —— "点不到"）。
     * 这一条**漏过一次**：`NpCanvasNode` 第一版只写 transform + z-index，
     * 于是框的样式在换画布时静默丢了，而它不会报错、只是点不动。
     */
    extraStyle?: object
  }>(),
  { selected: false, dragging: false, elevated: false, z: 0 },
)

/**
 * 层级 = **交互层 × 大格子 + 文档里记的次序**。
 *
 * ```
 *   交互层（此刻的）    2 = 拖动中（一定要在最上面，否则"拖到别人下面"看着像丢了）
 *                     1 = 被框住 / 悬停的临时抬升
 *                     0 = 其余
 *   z（文档里的）      选中一次就 +1，**不再掉回去**（用户 2027 要的）
 * ```
 *
 * ⚠️ 两者必须**分层**而不是相加：拖动中的节点哪怕 `z` 很小也要压过
 * 一切（不然"拖到别人下面"看着像丢了）；而同一个交互层里，谁在上面
 * 由文档里那个 `z` 说话。
 *
 * ⚠️ 用 `z-index` 而不是 DOM 顺序：重排 DOM 会让 `updateNodeInternals`
 * 量出来的顺序变、也会让 CSS 的 `:nth-child` 类样式漂。
 */
const Z_TIER = 100_000
const tier = computed(() => (props.dragging ? 2 : props.elevated ? 1 : 0))
const style = computed(() => ({
  /*
   * ⚠️ 顺序：定位在前、宿主的附加样式在后 —— 宿主**可以**覆盖定位
   * （虽然目前没人这么做），而反过来会让"框关掉指针事件"失效。
   */
  transform: `translate(${props.position.x}px, ${props.position.y}px)`,
  zIndex: String(tier.value * Z_TIER + Math.max(0, Math.round(props.z))),
  ...(props.extraStyle ?? {}),
}))
</script>

<template>
  <div
    class="np-graph__node"
    :class="{ 'is-selected': selected, 'is-dragging': dragging }"
    :data-id="id"
    :style="style"
  >
    <slot />
  </div>
</template>
